// Follow the bottom, until the reader moves away from it.
//
// Shared by `Conversation` and `PrivateChat` — same transcript shape, same rule, one hook
// rather than two copies of the same effect left to drift apart.
//
// The rule: scroll to the newest item when the reader is already near the bottom, and
// leave the scroll position alone otherwise — somebody who has scrolled up to read is not
// moved. Three cases have to come out right at once:
//
//   * FIRST PAINT. A thread with real history has to open on its newest message, not its
//     oldest. `previousHeight` starts at `0`, so the very first check finds the whole
//     scrollback "far away" from that non-existent previous height and follows
//     unconditionally — there is no such thing as "already near the bottom" of a scroller
//     nobody has looked at yet. Following used to mean "set `scrollTop` to `scrollHeight`",
//     which is the bottom of the SCROLLER — the same place as the top of the newest
//     MESSAGE whenever that message is shorter than the viewport, and a different place
//     once it is taller. See NEWEST ITEM, TOP OF below.
//
//   * THE READER'S OWN MESSAGE. By the time this effect runs, whatever just arrived is
//     already in the document, so the LIVE `scrollHeight` already counts its height. A
//     reply longer than the 200px allowance would cancel its own scroll for a reader who
//     was sitting at the bottom a moment before it landed. Measuring against
//     `previousHeight` — the height as of the last check, held in a ref — judges the
//     reader's position as it was before the new content arrived, which is the position
//     that actually decides whether they get followed.
//
//   * A PENDING ACTION ARRIVING ALONE. `lastKey` has to be the caller's own key from the
//     interleaved list of messages and actions, not a key derived from messages alone, or
//     an action with no accompanying message scrolls nothing.
//
// `useLayoutEffect`, not `useEffect`: the jump happens before the browser paints, so there
// is no visible flash of the old scroll position first.
//
// `resetKey` is a second, independent identity: the thread being read. Neither surface
// unmounts on a change of plan or thread — see `usePrivateThread`'s own generation
// counters, which exist for the same reason — so without this, `previousHeight` would
// carry a stale number from whichever thread was open before into the next one, and could
// misjudge that thread's own first paint. A change here throws the stored height away
// before it is used, which is what a fresh mount would have done for free.
//
// ---------------------------------------------------------------------------
// NEWEST ITEM, TOP OF — FIRST PAINT ONLY
// ---------------------------------------------------------------------------
//
// The roadmap item behind this hook opens a thread on its newest MESSAGE, not on the
// bottom of the SCROLLER — the two coincide only when that message is shorter than the
// viewport. `welcomes/preparing.md`, the authored welcome `createPlan` posts into a brand
// new plan's private thread, is usually neither: it is the only message the thread has,
// and it routinely runs taller than a phone screen, so landing at `scrollHeight` opened on
// its second half with nothing on screen to say a first half existed.
//
// So on first paint only, the scroll target is the TOP of the last rendered item —
// `node.scrollHeight` minus that item's own `offsetHeight` — rather than the foot of the
// scroller. The height comes off the DOM rather than off anything this hook knows about
// messages, because the newest item can be a pending action instead of one (see A PENDING
// ACTION ARRIVING ALONE above), and this hook has no idea what either renders. The item
// itself is found structurally rather than by name: `Conversation` and `PrivateChat` each
// put exactly one thing — the item list, or the empty-state paragraph — as the LAST child
// of the scroller, and that list's own last child is always the newest item (the "same
// transcript shape" this pair of surfaces shares — see the top of this file). Reaching it
// as `node.lastElementChild?.lastElementChild` costs this hook nothing in coupling to a
// class name from either surface.
//
// "First paint" here is NOT "`previousHeight.current` reads `0`" — that would be the
// EMPTY-STATE render `PrivateChat` produces before `usePrivateThread`'s own load resolves
// (see its header comment): a real render, of a real `scrollHeight`, with no item in it
// at all. If first paint were pinned to that render, the one that actually loads the
// thread's content would arrive with `previousHeight.current` already nonzero — from
// having just measured the empty scroller — and be judged NOT first paint, which is
// exactly backwards. So a second ref, `landedOnNewest`, tracks something else: whether
// this hook has already positioned the reader relative to an ACTUAL newest item since the
// last reset. It starts `false`, resets to `false` alongside `previousHeight` on a
// `resetKey` change, and only ever flips to `true` in the branch below that found a real
// item to land on — an empty-state render finds none, leaves it alone, and changes
// nothing about what the render after it is allowed to do.
//
// When there is no item at all — an empty thread, where the last child is the empty-state
// paragraph, which has no child elements — this falls back to the exact-bottom target, the
// old behaviour, and leaves `landedOnNewest` at `false`: nothing has actually been landed
// on yet, so the next render still gets to try once a real item exists.
//
// Deliberately NOT done for the other two cases. THE READER'S OWN MESSAGE and A PENDING
// ACTION ARRIVING ALONE still follow to the exact bottom: a reader already in a thread and
// watching it live expects new content to arrive flush with the bottom of the viewport,
// the way any other chat surface behaves, not to be pulled up to the top of whatever just
// landed out from under their own scroll position. `landedOnNewest` already reading `true`
// by the time either can happen is what keeps them on that path.
//
// ---------------------------------------------------------------------------
// `atBottom`
// ---------------------------------------------------------------------------
//
// The same "near the bottom" boolean that decides whether to auto-scroll, returned so a
// caller can act on it too — see `plan/useMarkThreadRead.ts`, which fires a mark-read call
// exactly when this is true. It starts `true`: the first check of a freshly mounted
// scroller always follows (see FIRST PAINT above), so a caller that reads it before that
// first layout effect has run must not default to "not at the bottom" and skip marking a
// thread that has just been opened.
//
// `near` — and so `atBottom` — is computed before NEWEST ITEM, TOP OF chooses where to
// land, and is unaffected by it: on first paint it reads `true` regardless of whether the
// newest item's own height puts the landing spot at the scroller's foot or partway up it.
// That is deliberate. Landing on the top of the newest message is still landing ON the
// newest message, which is what "a thread always opens scrolled to its newest loaded
// message" (`docs/archived/09-web-client.md`) means for the mark-read call this drives — the
// reader has been shown it, which is the only thing a self-facing, no-read-receipts unread
// marker is answering. Treating a first paint that lands mid-thread as "not caught up"
// would leave a brand-new plan's own preparing thread marked unread against the one
// message in it, which is a worse account of what the reader was shown than "they opened
// it and there it was".
//
// The layout effect above only RECOMPUTES it when `lastKey`/`resetKey` change — new
// content, or a new thread. A reader who scrolls away from the bottom with nothing new
// arriving would otherwise leave `atBottom` stuck at whatever it last was, which is wrong
// on its own terms and, through `useMarkThreadRead`, would keep firing mark-read calls for
// messages the reader has scrolled past rather than actually looked at. So a plain `scroll`
// listener tracks the CURRENT position live, independently of the follow/no-follow decision
// above: it never writes to `scrollTop` and never touches `previousHeight`, it only reports
// where the reader actually is right now.
//
// ---------------------------------------------------------------------------
// WHY `scroller` IS A CALLBACK REF, NOT A PLAIN `useRef`
// ---------------------------------------------------------------------------
//
// `Conversation` and `PrivateChat` both return `null` before their snapshot has loaded —
// there is no `<div>` for this hook to attach to yet, on the very first render(s) of the
// SAME mounted component instance. A plain `useRef` object does not itself cause a
// re-render when `.current` changes, so an effect that reads `scroller.current` and has no
// way to be told "the element just appeared" would see `null` on the run that happens to
// coincide with mount, and — with an empty dependency array — never run again once the
// snapshot loads and the real element exists. Routing the ref through a callback, and
// mirroring it into a bit of state, gives the live-scroll effect below a dependency that
// actually changes at the moment there is something to attach to.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type FollowBottomState = {
  /** Pass this straight to the scroller `<div>`'s `ref`. */
  readonly scroller: (node: HTMLDivElement | null) => void;
  readonly atBottom: boolean;
};

export function useFollowBottom(lastKey: string, resetKey: string): FollowBottomState {
  const element = useRef<HTMLDivElement | null>(null);
  const previousHeight = useRef(0);
  const previousResetKey = useRef(resetKey);
  // See NEWEST ITEM, TOP OF above: whether this hook has already landed the reader on a
  // real newest item since the last `resetKey` change. Distinct from `previousHeight`
  // reading `0` — that is also true of the empty-state render this pair of surfaces can
  // produce before their real content has loaded.
  const landedOnNewest = useRef(false);
  const [atBottom, setAtBottom] = useState(true);
  // Mirrors `element.current` into state purely so the effect below has something to
  // depend on. Read nowhere else — the layout effect and the scroll handler both still
  // read the ref directly, which is what stays correct even between this state's own
  // update and the render it causes.
  const [attached, setAttached] = useState<HTMLDivElement | null>(null);
  const scroller = useCallback((node: HTMLDivElement | null) => {
    element.current = node;
    setAttached(node);
  }, []);

  useLayoutEffect(() => {
    const node = element.current;
    if (node === null) {
      return;
    }
    if (previousResetKey.current !== resetKey) {
      previousResetKey.current = resetKey;
      previousHeight.current = 0;
      landedOnNewest.current = false;
    }
    const distance = previousHeight.current - node.scrollTop - node.clientHeight;
    const near = distance < 200;
    if (near) {
      const newestItem = node.lastElementChild?.lastElementChild ?? null;
      if (!landedOnNewest.current && newestItem instanceof HTMLElement) {
        node.scrollTop = node.scrollHeight - newestItem.offsetHeight;
        landedOnNewest.current = true;
      } else {
        node.scrollTop = node.scrollHeight;
      }
    }
    previousHeight.current = node.scrollHeight;
    setAtBottom(near);
  }, [lastKey, resetKey]);

  // The live tracker, re-attached whenever the element this hook is bound to actually
  // changes — including the very first time it exists at all.
  useEffect(() => {
    if (attached === null) {
      return undefined;
    }
    const onScroll = (): void => {
      const distance = attached.scrollHeight - attached.scrollTop - attached.clientHeight;
      setAtBottom(distance < 200);
    };
    attached.addEventListener('scroll', onScroll, { passive: true });
    return () => attached.removeEventListener('scroll', onScroll);
  }, [attached]);

  return { scroller, atBottom };
}
